import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_LAYOUT,
  externalUseRows,
  MAX_SLACK,
  type FlowSection,
  type LabelLayout,
  type LabelPage,
} from '@/features/dispensing-labels/layout';
import { leafletQrOf } from '@/features/dispensing-labels/printing';
import type {
  BagLabel,
  DispensingLabel,
  LabelSheetData,
} from '@/features/dispensing-labels/types';
import { useLabelPages } from './use-label-pages';
import styles from './LabelSheet.module.css';

const NO_SLACKS: number[] = [];
const DOSE_TIMES = ['morning', 'noon', 'evening', 'night'] as const;

/** CSS variables that size a card from its layout (default 70 x 40 mm). */
function layoutStyle(layout: LabelLayout): CSSProperties {
  return {
    '--label-w': `${layout.widthMm}mm`,
    '--label-h': `${layout.heightMm}mm`,
    '--label-pad': `${layout.paddingMm}mm`,
    '--label-font': `${layout.fontPt}pt`,
    '--label-qr': `${layout.qrMm}mm`,
  } as CSSProperties;
}

function FlowText({ section }: { section: FlowSection }) {
  switch (section.key) {
    case 'directionsBm':
      return <p lang="ms">{section.text}</p>;
    case 'warnings':
    case 'storage':
      return <p className={styles.warning}>{section.text}</p>;
    case 'meta':
      return <p className={styles.meta}>{section.text}</p>;
    default:
      return <p>{section.text}</p>;
  }
}

/**
 * One medicine label (default 70 x 40 mm). A label whose text does not fit is
 * split over continuation labels ("2/3"): the controlled / external-use
 * banners repeat on each, the leaflet QR and dose times are on the first.
 * `marked` adds the TEST / STAGING band (and TEST placeholders for external-use
 * wording that is not approved yet); production passes false.
 */
export function LabelCard({
  label,
  images,
  marked,
  layout = DEFAULT_LAYOUT,
  page,
  index,
}: {
  label: DispensingLabel;
  images: Record<string, string>;
  marked: boolean;
  layout?: LabelLayout;
  page: LabelPage;
  /** Position of the label in the sheet (used to measure and re-split it). */
  index?: number;
}) {
  const { t } = useTranslation();
  const leafletUrl = page.first ? leafletQrOf(label) : null;
  const qrSrc = leafletUrl ? images[leafletUrl] : undefined;
  const dose = DOSE_TIMES.map((time, index) => ({
    time,
    on: label.pictogram[index] === true,
  }));
  const multi = page.total > 1;
  const extraRows = label.externalUse ? externalUseRows(marked) : [];

  return (
    <article
      className={styles.label}
      style={layoutStyle(layout)}
      aria-label={
        multi ? `${label.medicine} ${page.index}/${page.total}` : label.medicine
      }
      data-label-card=""
      data-label-index={index}
      data-label-page={`${page.index}/${page.total}`}
    >
      {marked && (
        <p className={styles.band}>{t('dispensingLabels.nonProductionBand')}</p>
      )}
      {page.first && (
        <header className={styles.clinic}>
          <strong>{label.clinic.name}</strong>
          {label.clinic.address && <span> · {label.clinic.address}</span>}
          {label.clinic.phone && <span> · {label.clinic.phone}</span>}
        </header>
      )}
      <div className={styles.body}>
        <div className={styles.text}>
          <p className={styles.medicine}>
            {label.medicine}
            {multi && ` · ${page.index}/${page.total}`}
          </p>
          <p>
            <strong>{label.patient}</strong> ·{' '}
            {t('dispensingLabels.rxLine', {
              number: label.rxNumber,
              date: label.date,
            })}
          </p>
          {page.first && (
            <ul
              className={styles.dose}
              aria-label={t('dispensingLabels.dosePictogram')}
            >
              {dose.map(({ time, on }) => (
                <li
                  key={time}
                  className={on ? styles.doseOn : styles.doseOff}
                  aria-label={`${t(`dispensingLabels.doseTimes.${time}`)}: ${on ? 'yes' : 'no'}`}
                >
                  {t(`dispensingLabels.doseTimes.${time}`)}
                </li>
              ))}
            </ul>
          )}
          {page.sections.map((section, index) => (
            <FlowText key={`${section.key}-${index}`} section={section} />
          ))}
        </div>
        {leafletUrl && (
          <div className={styles.qr}>
            {qrSrc ? (
              <>
                <img src={qrSrc} alt={t('dispensingLabels.scanForLeaflet')} />
                <span>{t('dispensingLabels.scanForLeaflet')}</span>
                <span lang="ms">{t('dispensingLabels.scanForLeafletMs')}</span>
              </>
            ) : (
              <span role="alert">{t('dispensingLabels.qrFailed')}</span>
            )}
          </div>
        )}
      </div>
      {label.controlled && (
        <p className={styles.controlled}>{t('dispensingLabels.controlled')}</p>
      )}
      {label.externalUse && (
        <>
          <p className={styles.controlled}>
            {t('dispensingLabels.externalUse')}
          </p>
          {extraRows.map(({ langs, text }) => (
            <p
              key={langs.join('-')}
              lang={text ? langs[0] : undefined}
              className={text ? styles.controlled : styles.placeholder}
              data-test-placeholder={text ? undefined : langs.join(',')}
            >
              {text ?? t('dispensingLabels.externalUsePlaceholder')}
            </p>
          ))}
        </>
      )}
    </article>
  );
}

/** A label with all its pages, in order; `forPrint` puts each on its own printed page. */
export function LabelPages({
  label,
  images,
  marked,
  layout,
  slack = 0,
  index,
  forPrint = false,
}: {
  label: DispensingLabel;
  images: Record<string, string>;
  marked: boolean;
  layout: LabelLayout;
  slack?: number;
  index?: number;
  forPrint?: boolean;
}) {
  const pages = useLabelPages(label, layout, marked, slack);
  return (
    <>
      {pages.map((page) => {
        const card = (
          <LabelCard
            key={page.index}
            label={label}
            images={images}
            marked={marked}
            layout={layout}
            page={page}
            index={index}
          />
        );
        return forPrint ? (
          <div key={page.index} className={styles.printPage}>
            {card}
          </div>
        ) : (
          card
        );
      })}
    </>
  );
}

export function BagLabelCard({
  bag,
  marked,
  layout = DEFAULT_LAYOUT,
}: {
  bag: BagLabel;
  marked: boolean;
  layout?: LabelLayout;
}) {
  const { t } = useTranslation();
  return (
    <article
      className={`${styles.label} ${styles.bag}`}
      style={layoutStyle(layout)}
      aria-label={t('dispensingLabels.bagLabel')}
    >
      {marked && (
        <p className={styles.band}>{t('dispensingLabels.nonProductionBand')}</p>
      )}
      <p className={styles.medicine}>{t('dispensingLabels.bagLabel')}</p>
      <p>
        <strong>{bag.patient}</strong> · {bag.rxNumber}
      </p>
      <p className={styles.meta}>{t('dispensingLabels.bagItems')}</p>
      <ul className={styles.bagItems}>
        {bag.items.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    </article>
  );
}

/**
 * Screen preview. Outside production it is hidden from the printed page, so a
 * browser-level print does not produce label pages (best effort, not a lock).
 */
export function LabelPreview({
  sheet,
  images,
  marked,
  layout = DEFAULT_LAYOUT,
  onFit,
}: {
  sheet: LabelSheetData;
  images: Record<string, string>;
  marked: boolean;
  layout?: LabelLayout;
  /** Reports the settled layout: lines kept free, and cards that still clip. */
  onFit?: (fit: { slacks: number[]; clipped: number }) => void;
}) {
  const { t } = useTranslation();
  const cards = useRef<HTMLDivElement>(null);
  // Lines kept free per label, valid for this size and sheet only: a new size
  // or sheet starts from the estimate again.
  const [fitState, setFitState] = useState<{
    sheet: LabelSheetData;
    layoutId: string;
    slacks: number[];
  } | null>(null);
  const slacks =
    fitState?.sheet === sheet && fitState.layoutId === layout.id
      ? fitState.slacks
      : NO_SLACKS;
  const [clipped, setClipped] = useState(0);

  // The page split is an estimate. Measure the real cards: for each label with
  // a card that still has text that does not fit, keep one more line free and
  // split that label again (bounded). If some still clip at the limit, say so
  // and let the dialog block printing.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const boxes =
        cards.current?.querySelectorAll<HTMLElement>('[data-label-card]');
      let count = 0;
      const next = [...slacks];
      let raised = false;
      boxes?.forEach((box) => {
        if (box.scrollHeight <= box.clientHeight + 1) return;
        const at = Number(box.dataset.labelIndex);
        if (Number.isInteger(at) && (next[at] ?? 0) < MAX_SLACK) {
          next[at] = (next[at] ?? 0) + 1;
          raised = true;
        } else count += 1;
      });
      if (raised) {
        setFitState({ sheet, layoutId: layout.id, slacks: next });
        return;
      }
      setClipped(count);
      onFit?.({ slacks, clipped: count });
    });
    return () => cancelAnimationFrame(frame);
  }, [sheet, images, layout, slacks, onFit]);

  return (
    <div className={marked ? styles.screenOnly : undefined}>
      {clipped > 0 && (
        <p className={styles.overflowWarning} role="alert">
          {t('dispensingLabels.labelOverflow', { count: clipped })}
        </p>
      )}
      <div className={styles.cards} ref={cards}>
        {sheet.labels.map((label, index) => (
          <LabelPages
            key={`${label.rxNumber}-${index}`}
            label={label}
            images={images}
            marked={marked}
            layout={layout}
            slack={slacks[index] ?? 0}
            index={index}
          />
        ))}
        <BagLabelCard bag={sheet.bagLabel} marked={marked} layout={layout} />
      </div>
    </div>
  );
}

/** Production only: the sheet laid out for the label printer, then print once. */
export function LabelPrintRoot({
  sheet,
  images,
  onDone,
  layout = DEFAULT_LAYOUT,
  slacks = [],
}: {
  sheet: LabelSheetData;
  images: Record<string, string>;
  onDone: () => void;
  layout?: LabelLayout;
  /** Per label, the lines the preview found it needed to keep free (same layout and text). */
  slacks?: number[];
}) {
  useEffect(() => {
    // The named page takes the chosen layout's size (the static CSS rule is the
    // 70 x 40 mm default); removed again once printing is over.
    const style = document.createElement('style');
    style.setAttribute('data-label-page-size', '');
    style.textContent = `@media print { @page dispensing-label { size: ${layout.widthMm}mm ${layout.heightMm}mm; margin: 0; } }`;
    document.head.appendChild(style);
    window.addEventListener('afterprint', onDone, { once: true });
    window.print();
    return () => {
      window.removeEventListener('afterprint', onDone);
      style.remove();
    };
  }, [onDone, layout]);

  return createPortal(
    <div
      className={styles.printRoot}
      data-label-print-root=""
      data-testid="label-print-sheet"
    >
      {sheet.labels.map((label, index) => (
        <LabelPages
          key={`${label.rxNumber}-${index}`}
          label={label}
          images={images}
          marked={false}
          layout={layout}
          slack={slacks[index] ?? 0}
          index={index}
          forPrint
        />
      ))}
      <div className={styles.printPage}>
        <BagLabelCard bag={sheet.bagLabel} marked={false} layout={layout} />
      </div>
    </div>,
    document.body,
  );
}
